import { useEffect, useReducer, useRef } from 'react';

import type { AdPlatform } from '@/contracts/onboarding-session';

import {
  adDiscoveryReducer,
  initialAdDiscoveryState,
  type AdDiscoveryState,
} from '../ads/adDiscovery.reducer';
import { getAdEvents } from '../services/onboarding-session.service';
import type { RecoverSession } from './useSessionRecovery';
import { isSessionNotFound, SNAPSHOT_BACKOFF_MS, SNAPSHOT_POLL_MS } from './useSessionSnapshot';

/**
 * Consecutive lost-session recoveries after which the feed gives up and settles as `unavailable` (or `partial`
 * when ads had arrived); the snapshot heartbeat keeps trying on its own.
 */
export const MAX_FEED_RECOVERIES = 3;

export interface UseAdDiscoveryEventsOptions {
  sessionId: string;
  platform: AdPlatform;
  enabled: boolean;
  /**
   * Called when the BFF no longer knows the session (404 `session_not_found`). The feed stops asking, waits for
   * this to bring the session back, then starts over from the first event. Without it the feed just stops.
   */
  recover?: RecoverSession;
}

const tabHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * One platform's ad discovery feed: polls `GET /ads/:platform/events?since=` from the cursor the ported
 * reducer holds (`lastSequence`), folds the events in (dedupe by sequence, revision wins, terminal final)
 * and stops once the feed is terminal. A `reset` from the BFF drops the cursor and the state and starts over.
 * Failures back off 1/3/8s while the component is mounted. Like the snapshot poll it does not run in a hidden
 * tab: it waits for the tab to come back and then asks at once. A 404 `session_not_found` is not a failure to
 * back off from: the feed hands it to `recover` and restarts from zero once the session is back.
 */
export function useAdDiscoveryEvents({ sessionId, platform, enabled, recover }: UseAdDiscoveryEventsOptions): AdDiscoveryState {
  const [state, dispatch] = useReducer(adDiscoveryReducer, initialAdDiscoveryState);
  // Read at call time: a new `recover` function is not a reason to restart the feed.
  const recoverRef = useRef(recover);
  recoverRef.current = recover;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let removeVisibilityListener: (() => void) | undefined;
    // The cursor lives in the loop, not in React state, so the next request never waits on a render.
    let since = 0;
    let failures = 0;
    let recoveries = 0;
    dispatch({ type: 'reset' });

    const schedule = (delay: number) => {
      timer = setTimeout(poll, delay);
    };

    const poll = async () => {
      if (cancelled) return;
      if (tabHidden()) {
        const onVisible = () => {
          removeVisibilityListener?.();
          void poll();
        };
        document.addEventListener('visibilitychange', onVisible);
        removeVisibilityListener = () => {
          document.removeEventListener('visibilitychange', onVisible);
          removeVisibilityListener = undefined;
        };
        return;
      }
      let delay: number | null = SNAPSHOT_POLL_MS;
      try {
        const page = await getAdEvents(sessionId, platform, since > 0 ? since : undefined);
        if (cancelled) return;
        failures = 0;
        recoveries = 0;
        if (page.reset) {
          since = 0;
          dispatch({ type: 'reset' });
        } else {
          for (const event of page.events) dispatch({ type: 'event', event });
          since = page.nextSince;
          if (page.terminal) {
            dispatch({ type: 'finished', jobStatus: page.status });
            delay = null;
          }
        }
        if (page.reset) delay = 0;
      } catch (error) {
        if (cancelled) return;
        if (isSessionNotFound(error)) {
          // Not a flaky network: the session is gone. Stop asking and defer to the recovery.
          const recoverNow = recoverRef.current;
          if (!recoverNow || recoveries >= MAX_FEED_RECOVERIES) {
            // Giving up: end the feed in a terminal state so the ads card stops spinning (ads, if any, are kept).
            dispatch({ type: 'finished', jobStatus: 'unavailable' });
            return;
          }
          recoveries += 1;
          try {
            await recoverNow();
          } catch {
            // Recovery failed (BFF down): fall through to the normal backoff below.
          }
          if (cancelled) return;
          since = 0;
          dispatch({ type: 'reset' });
          failures += 1;
          schedule(SNAPSHOT_BACKOFF_MS[Math.min(failures, SNAPSHOT_BACKOFF_MS.length) - 1]!);
          return;
        }
        failures += 1;
        delay = SNAPSHOT_BACKOFF_MS[Math.min(failures, SNAPSHOT_BACKOFF_MS.length) - 1]!;
      }
      if (!cancelled && delay !== null) schedule(delay);
    };
    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      removeVisibilityListener?.();
    };
  }, [sessionId, platform, enabled]);

  return state;
}
